import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  teamMembers as fallbackTeam,
  featuredTeamMembers as fallbackFeatured,
  type TeamMember,
} from "@/data/teamMembers";

interface SocialLinks {
  linkedin?: string;
  instagram?: string;
  facebook?: string;
  website?: string;
}

interface DbTeamMember {
  id: string;
  name: string;
  slug: string;
  title: string | null;
  short_bio: string | null;
  bio: string | null;
  headshot_url: string | null;
  email: string | null;
  phone: string | null;
  specialties: string[] | null;
  service_areas: string[] | null;
  social_links: SocialLinks | null;
  is_featured: boolean;
  sort_order: number;
}

const SOCIAL_ORDER: Array<{ key: keyof SocialLinks; label: string }> = [
  { key: "facebook", label: "Facebook" },
  { key: "instagram", label: "Instagram" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "website", label: "Website" },
];

const mapDbToTeamMember = (row: DbTeamMember): TeamMember => {
  const social = row.social_links ?? {};
  const socialLinks = SOCIAL_ORDER.flatMap(({ key, label }) => {
    const href = social[key];
    return href ? [{ label, href }] : [];
  });
  // Fall back to bundled image if member exists in fallback by slug
  const fallbackMatch = fallbackTeam.find((m) => m.slug === row.slug);
  return {
    id: row.id,
    name: row.name,
    slug: row.slug,
    title: row.title ?? "",
    email: row.email ?? "",
    phone: row.phone ?? "",
    image: row.headshot_url || fallbackMatch?.image || "",
    imageAlt: `${row.name}${row.title ? `, ${row.title}` : ""} at Hadassah Homes`,
    profileUrl: `/team/${row.slug}`,
    socialLinks,
    specialties: row.specialties ?? [],
    areaServed: row.service_areas ?? [],
    featured: row.is_featured,
    displayOrder: row.sort_order,
  };
};

export function useTeamMembers(options: { featuredOnly?: boolean } = {}) {
  const [members, setMembers] = useState<TeamMember[]>(
    options.featuredOnly ? fallbackFeatured : fallbackTeam
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("team_members")
          .select(
            "id, name, slug, title, short_bio, bio, headshot_url, email, phone, specialties, service_areas, social_links, is_featured, sort_order"
          )
          .eq("is_published", true)
          .order("sort_order", { ascending: true })
          .order("name", { ascending: true });
        if (cancelled) return;
        if (error || !data || data.length === 0) {
          setLoading(false);
          return;
        }
        let mapped = (data as unknown as DbTeamMember[]).map(mapDbToTeamMember);
        if (options.featuredOnly) {
          const featured = mapped.filter((m) => m.featured);
          if (featured.length > 0) mapped = featured;
        }
        setMembers(mapped);
      } catch (err) {
        console.error("Error fetching team members:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [options.featuredOnly]);

  return { members, loading };
}
